// CP6 (H1.F) — Sentry init. @sentry/react patches global error handlers in
// init(); for the browser SDK only init-before-render() matters (no
// patch-before-framework-import requirement like @sentry/node), so inline here.
import * as Sentry from "@sentry/react";

// VITE_* keys are read off a cast import.meta — they are not in the typed
// ImportMetaEnv augmentation (same pattern as EmailCaptureModal's VITE_TENANT_HOST).
const env = (import.meta as any).env || {};

Sentry.init({
  dsn: env.VITE_SENTRY_DSN,
  release: env.VITE_SENTRY_RELEASE || "unknown",
  environment: env.MODE,
  tracesSampleRate: 0.1,
  integrations: [Sentry.browserTracingIntegration()],
  ignoreErrors: [
    /rate_limited/,
    /origin_blocked/,
  ],
  beforeSend(event) {
    if (event.extra) {
      delete (event.extra as any).email;
    }
    return event;
  },
});

if (!env.VITE_SENTRY_DSN) {
  console.log("[sentry] disabled (no DSN)");
}

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { AuthProvider } from './lib/auth.tsx';
import { loadOperatorCache } from './data/operators';

async function bootstrap() {
  // Preload approved operators so operator doors are branded on first paint.
  // Bounded by a 2s timeout so a slow/hanging DB never blanks the screen — on
  // timeout we render with the static map and the cache fills on the next load.
  await Promise.race([
    loadOperatorCache(),
    new Promise((r) => setTimeout(r, 2000)),
  ]);

  createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', color: '#64748b', gap: 12 }}>
          <div style={{ fontSize: 32 }}>⚠️</div>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>Something went wrong</div>
          <div style={{ fontSize: 13 }}>{error instanceof Error ? error.message : String(error)}</div>
          <button onClick={() => window.location.reload()} style={{ marginTop: 8, padding: '8px 20px', background: '#0f172a', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
            Reload
          </button>
        </div>
      )}
    >
      <AuthProvider>
        <App />
      </AuthProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
  );
}

bootstrap();
